/**
 * Toast уведомление о новом сообщении (TODO-018b)
 * Дата: 15.01.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/contexts/WebSocketContext.tsx` — формирует toast данные и обработчики
 * - `frontend/src/pages/Messages.tsx` — показывает toast и обрабатывает "Открыть"
 *
 * КАК РАБОТАЕТ (кратко):
 * - Показывает превью нового сообщения и позволяет открыть соответствующую беседу
 *
 * СХЕМА:
 * - Композиция функций:
 *   - NewMessageToast(props) -> render -> onOpenConversation/onClose
 * - Использование из других модулей:
 *   - MessagesPage
 */
import { X } from 'lucide-react';
import type { NewMessageToastData } from '@/contexts/WebSocketContext';
import { cn } from '@/utils/cn';

interface NewMessageToastProps {
  toast: NewMessageToastData | null;
  onOpenConversation: () => void;
  onClose: () => void;
}

export function NewMessageToast({ toast, onOpenConversation, onClose }: NewMessageToastProps) {
  if (!toast) return null;

  const p = toast.payload;
  const preview = (p.content || '').slice(0, 120);
  const channel = (p.channel || '').toUpperCase();

  return (
    <div
      className={cn(
        'fixed top-20 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)]',
        'rounded-lg border border-neutral-200 bg-white shadow-soft'
      )}
      data-testid="toast-new-message"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-neutral-900">Новое сообщение</div>
          <div className="mt-1 text-xs text-neutral-500">
            От: {p.sender_name || p.phone_normalized || p.phone} • Канал: {channel || '—'}
          </div>
          <div className="mt-2 text-sm text-neutral-800">{preview}{p.content?.length > 120 ? '…' : ''}</div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onOpenConversation}
              className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors"
              data-testid="toast-new-message-open"
            >
              Открыть
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md bg-neutral-100 text-neutral-700 text-xs font-medium hover:bg-neutral-200 transition-colors"
              data-testid="toast-new-message-dismiss"
            >
              Закрыть
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-neutral-100 transition-colors"
          aria-label="Close"
          data-testid="toast-new-message-close"
        >
          <X className="w-4 h-4 text-neutral-500" />
        </button>
      </div>
    </div>
  );
}

